import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { PlayIcon } from "@phosphor-icons/react/dist/csr/Play";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { EmptyMachine, FURNACE } from "@volca/ui/components/ui/empty-machine";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import { PayrollRunStatusBadge } from "@/payroll/pay-periods/payroll-run-status-badge";
import { PayrollRunStatus } from "@/payroll/payroll.d";

interface AiScriptRunStateProps {
  title: string;
  description?: string;
  action: "run" | "download";
  status?: PayrollRunStatus | null;
  isLoading?: boolean;
  disabledReason?: string;
  onAction: () => void;
}

export const AiScriptRunState = ({
  title,
  description,
  action,
  status,
  isLoading = false,
  disabledReason,
  onAction,
}: AiScriptRunStateProps) => {
  const isDownloadAction = action === "download";
  const isRunDisabled = !isDownloadAction && (isLoading || !!disabledReason);
  const isFailed = status === PAYROLL_RUN_STATUS.failed;

  const actionButton = (
    <Button
      variant="default"
      onClick={onAction}
      disabled={isRunDisabled || (isDownloadAction && isLoading)}
      aria-busy={(isDownloadAction && isLoading) || undefined}
    >
      {isDownloadAction ? (
        <>
          {!isLoading && (
            <DownloadSimpleIcon data-icon="inline-start" className="h-4 w-4" />
          )}
          Download result
        </>
      ) : (
        <>
          {!isLoading && (
            <PlayIcon data-icon="inline-start" className="h-4 w-4" />
          )}
          Run payroll
        </>
      )}
    </Button>
  );

  if (isFailed)
    return (
      <ErrorState
        title={title}
        description={disabledReason ?? description}
        retrying={isLoading}
      >
        {actionButton}
      </ErrorState>
    );

  return (
    <Empty
      data-machine-host
      className={emptyStateVariants({ layout: "panel" })}
    >
      <EmptyHeader>
        <EmptyMedia>
          <EmptyMachine art={FURNACE} active={isLoading} />
        </EmptyMedia>
        {status && <PayrollRunStatusBadge status={status} />}
        <EmptyTitle>{title}</EmptyTitle>
        {!isDownloadAction && disabledReason ? (
          <EmptyDescription>{disabledReason}</EmptyDescription>
        ) : (
          description && <EmptyDescription>{description}</EmptyDescription>
        )}
      </EmptyHeader>
      <EmptyContent data-machine-trigger>{actionButton}</EmptyContent>
    </Empty>
  );
};
